/**
 * Procedural Web Audio Sound Effects for Chess
 * Lightweight synthesizer for move taps, captures, checks, and game over.
 */

class ChessAudio {
  constructor() {
    this.ctx = null
    this.muted = false
  }

  init() {
    if (!this.ctx && typeof window !== 'undefined') {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (AudioCtx) {
        this.ctx = new AudioCtx()
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {})
    }
  }

  playMove() {
    if (this.muted) return
    this.init()
    if (!this.ctx) return

    try {
      const now = this.ctx.currentTime
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()

      osc.type = 'triangle'
      osc.frequency.setValueAtTime(320, now)
      osc.frequency.exponentialRampToValueAtTime(140, now + 0.06)

      gain.gain.setValueAtTime(0.3, now)
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.06)

      osc.connect(gain)
      gain.connect(this.ctx.destination)

      osc.start(now)
      osc.stop(now + 0.07)
    } catch {
      // Audio failure fallback
    }
  }

  playCapture() {
    if (this.muted) return
    this.init()
    if (!this.ctx) return

    try {
      const now = this.ctx.currentTime

      // Punchy click
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()

      osc.type = 'sine'
      osc.frequency.setValueAtTime(450, now)
      osc.frequency.exponentialRampToValueAtTime(80, now + 0.09)

      gain.gain.setValueAtTime(0.5, now)
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.09)

      osc.connect(gain)
      gain.connect(this.ctx.destination)

      osc.start(now)
      osc.stop(now + 0.1)

      // Sub thud
      const sub = this.ctx.createOscillator()
      const subGain = this.ctx.createGain()
      sub.type = 'triangle'
      sub.frequency.setValueAtTime(180, now)
      sub.frequency.exponentialRampToValueAtTime(50, now + 0.12)

      subGain.gain.setValueAtTime(0.4, now)
      subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12)

      sub.connect(subGain)
      subGain.connect(this.ctx.destination)

      sub.start(now)
      sub.stop(now + 0.13)
    } catch {
      // Audio failure fallback
    }
  }

  playCheck() {
    if (this.muted) return
    this.init()
    if (!this.ctx) return

    try {
      const now = this.ctx.currentTime
      const freqs = [587.33, 880] // D5, A5
      freqs.forEach((freq, idx) => {
        const osc = this.ctx.createOscillator()
        const gain = this.ctx.createGain()

        osc.type = 'sine'
        osc.frequency.setValueAtTime(freq, now + idx * 0.08)

        gain.gain.setValueAtTime(0.25, now + idx * 0.08)
        gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.08 + 0.25)

        osc.connect(gain)
        gain.connect(this.ctx.destination)

        osc.start(now + idx * 0.08)
        osc.stop(now + idx * 0.08 + 0.26)
      })
    } catch {
      // Audio failure fallback
    }
  }

  playGameOver() {
    if (this.muted) return
    this.init()
    if (!this.ctx) return

    try {
      const now = this.ctx.currentTime
      const chord = [392, 493.88, 587.33, 783.99] // G major chord
      chord.forEach((freq, idx) => {
        const osc = this.ctx.createOscillator()
        const gain = this.ctx.createGain()

        osc.type = 'triangle'
        osc.frequency.setValueAtTime(freq, now + idx * 0.1)

        gain.gain.setValueAtTime(0.2, now + idx * 0.1)
        gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.1 + 0.6)

        osc.connect(gain)
        gain.connect(this.ctx.destination)

        osc.start(now + idx * 0.1)
        osc.stop(now + idx * 0.1 + 0.65)
      })
    } catch {
      // Audio failure fallback
    }
  }

  destroy() {
    if (this.ctx) {
      try {
        this.ctx.close()
      } catch {
        // Safe disposal
      }
      this.ctx = null
    }
  }
}

export const chessAudio = new ChessAudio()
